import React, { useMemo, useState } from 'react'

import {
  MapPinIcon,
  PlusIcon,
  CircleCheckBigIcon,
  PenIcon,
  TrashIcon,
  SearchIcon,
} from 'lucide-react'

import { useTheme, Box, Stack, Typography } from '@mui/material'

import { Button, Card, Input, Skeleton } from '@repo/ui'
import {
  EmptyState,
  IntroDataBanner,
  Loader,
  ActionDropdown,
  CreateAddressModal,
  DeleteAddressModal,
  SetDefaultAddressModal,
  StatusChip,
  AddressPreview,
  UserCell,
  UserAvatarGroup,
} from 'components'
import { ExampleKindBadge } from 'components/display/ExampleKindBadge'
import { useAddresses, useCompanyAddressManagers, useCompanyUsers } from 'hooks/api'
import { getPersonLabel } from 'utils'
import { ADDRESS_STATUSES } from 'constants/statuses'
import { ADDRESS_TYPE_LABELS, type Address } from 'types'

export const AddressBookPage = () => {
  const theme = useTheme()
  const { addresses, isLoading, refetch } = useAddresses()

  /*
    VRG-528 — who manages each location, shown on its card.

    TWO requests for the whole page, not two per card. `managersByAddressId` is one bulk
    read (`GET /addresses/managers`) rather than `useAddressManagers` per row, which would
    be an N+1 storm — and worse than the count suggests, because each would be its own
    `useQuery`: N cache entries, N loading states, cards resolving one at a time down the
    page. The member list is the second request, and it is the SAME one the edit modal
    already loads, so react-query serves it from cache when the modal opens (identical key
    and params) rather than fetching it twice.

    `limit: 200` mirrors the modal's override of the hook's default 50 — that default is a
    page size for a paginated table, and a company with more staff than that would render
    a manager as a blank avatar with nothing on screen saying why.
  */
  const { managersByAddressId, isLoading: isLoadingManagers } = useCompanyAddressManagers()
  const { data: companyUsersData, isLoading: isLoadingUsers } = useCompanyUsers({ limit: 200 })
  const isLoadingManagerDetail = isLoadingManagers || isLoadingUsers

  const usersById = useMemo(() => {
    const map = new Map<string, { id?: string; name?: string; email?: string }>()
    for (const user of companyUsersData?.data ?? []) {
      const { primary } = getPersonLabel(user)
      // `name` is what `UserAvatarGroup` derives initials AND the tooltip from, so it is
      // resolved through the same helper `UserBlock` uses rather than by joining the name
      // parts here — a member invited by email has no name at all, and a local join would
      // hand the avatar an empty string and render the anonymous glyph for a person we can
      // perfectly well identify.
      map.set(user.id, { id: user.id, name: primary ?? undefined, email: user.email })
    }
    return map
  }, [companyUsersData])

  /*
    ABSENT MEANS NONE. The endpoint omits an address with no managers rather than sending
    an empty array, so `?? []` is the read — and that empty case is the one worth showing:
    a location with no manager can never have an arrival confirmed by scan, which is the
    whole point of the field.

    Ids with no matching member are dropped rather than drawn as blanks. That happens when
    someone has left the company between the two queries; a faceless circle would claim a
    manager exists.
  */
  const managersFor = (addressId: string) =>
    (managersByAddressId[addressId] ?? [])
      .map(userId => usersById.get(userId))
      .filter((user): user is { id?: string; name?: string; email?: string } => Boolean(user))

  const [search, setSearch] = useState('')
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null)
  const [openAddressModal, setOpenAddressModal] = useState(false)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [openSetDefaultModal, setOpenSetDefaultModal] = useState(false)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return addresses
    return addresses.filter(a =>
      [a.label, a.full_name, a.city, a.state, a.address_line, ADDRESS_TYPE_LABELS[a.type]]
        .filter(Boolean)
        .some(value => String(value).toLowerCase().includes(q)),
    )
  }, [addresses, search])

  const handleAdd = () => {
    setSelectedAddress(null)
    setOpenAddressModal(true)
  }

  const handleEdit = (address: Address) => {
    setSelectedAddress(address)
    setOpenAddressModal(true)
  }

  const handleSetDefault = (address: Address) => {
    setSelectedAddress(address)
    setOpenSetDefaultModal(true)
  }

  const handleDelete = (address: Address) => {
    setSelectedAddress(address)
    setOpenDeleteModal(true)
  }

  const getActionItems = (address: Address) => [
    { label: 'Edit', onClick: () => handleEdit(address), icon: <PenIcon strokeWidth={1} /> },
    {
      label: 'Set as default',
      onClick: () => handleSetDefault(address),
      visible: !address.is_default,
      icon: <CircleCheckBigIcon strokeWidth={1} />,
    },
    {
      label: 'Delete',
      onClick: () => handleDelete(address),
      divider: true,
      destructive: true,
      icon: <TrashIcon strokeWidth={1} />,
    },
  ]

  return (
    <Stack spacing={4}>
      {/*
        VRG-512: the sample-data caveat — see `IntroDataBanner`'s own docblock. The intro
        seed writes three sample addresses fenced by `addresses.example_kind`, and each row
        carries its own `ExampleKindBadge` below; this is the page-level half of the same
        statement, and the only place the "clear sample data" action is reachable from this
        surface. `nouns="addresses"` because the default copy names products, certificates
        and scans, none of which are what a reader is looking at here.

        Inside the `Stack spacing={4}` rather than outside it. An earlier revision placed it
        outside to stop the Stack's 16px gap stacking on the banner's own `mb: 6` (40px where
        every other surface has 24) — but that was written against a page whose root was a
        fragment. Main's `VRG-528` restructure made the root the Stack itself, so "outside"
        no longer exists without reintroducing a wrapper. The banner's own margin is the one
        to change if the gap reads wrong; do not add a second one here.
      */}
      <IntroDataBanner nouns="addresses" />

      <Stack direction="row" spacing={3} alignItems="center" justifyContent="space-between">
        <Box sx={{ maxWidth: 420, flex: 1 }}>
          <Input
            placeholder="Search"
            value={search}
            onValueChange={setSearch}
            startAdornment={<SearchIcon strokeWidth={1} size={18} />}
          />
        </Box>
        <Button
          variant="primary"
          disableElevation
          onClick={handleAdd}
          startIcon={<PlusIcon strokeWidth={1} />}
          sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
        >
          Add new address
        </Button>
      </Stack>

      {isLoading ? (
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '55vh' }}
        >
          <Loader />
        </Box>
      ) : filtered.length === 0 ? (
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '55vh' }}
        >
          <EmptyState
            title={search ? 'No matching addresses' : 'No addresses added'}
            text={
              search
                ? 'Try a different search term.'
                : 'Use the Add new address action above to save a fulfillment location.'
            }
            icon={<MapPinIcon strokeWidth={1} size={32} />}
          />
        </Box>
      ) : (
        // Match the Payment Methods list: primary[100]-bordered container,
        // full-width divider rows (no outer padding), roomier row padding.
        <Card sx={{ p: 0, border: `1px solid ${theme.palette.primary[100]}` }}>
          <Stack divider={<Box sx={{ borderBottom: `1px solid ${theme.palette.primary[100]}` }} />}>
            {filtered.map(address => (
              <Stack
                key={address.id}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 6, py: 4 }}
              >
                {/*
                  §36: was a hand-rolled avatar (44px rounded square, grey[300]
                  border, default-size icon) + its own title/subtitle formatters.
                  Converged onto the shared `AddressPreview` (40px circle,
                  primary[100] border, 20px icon) so this reads identically to
                  the picker in `SendToLocationModal` and the table `AddressCell`.
                  The "default" chip moves alongside the preview rather than
                  inline with the title — same arrangement `AddressCell` uses.
                */}
                {/*
                  `flex: 1` so the address absorbs ALL the slack and the manager column
                  lands at the same x on every row. Without it the row's
                  `space-between` positioned the middle child off the FIRST child's
                  width, so a long address pushed its managers right and the column
                  zig-zagged down the list — measured 556 against 598 between two rows.
                  A labelled attribute that does not line up with the one above it does
                  not read as a column at all.
                */}
                <Stack
                  spacing={1}
                  justifyContent="center"
                  sx={{ flex: '0 1 46%', minWidth: 0 }}
                >
                  {/*
                    BOTH columns are labelled, not just the managers one. A single
                    labelled column beside an unlabelled one reads as an annotation on
                    the odd one out; two labels read as a header row, which is what this
                    list actually is. Same `body4`/secondary treatment and the same
                    label→content gap on each, so the two baselines line up across the
                    card rather than merely being present.
                  */}
                  <Typography variant="body4" color="text.secondary" noWrap>
                    Location
                  </Typography>
                  <Stack direction="row" spacing={3} alignItems="center" minWidth={0}>
                    <AddressPreview address={address} />
                    {address.is_default && (
                      <StatusChip statuses={ADDRESS_STATUSES} status="default" size="small" />
                    )}
                    {/*
                      VRG-512: a BADGE beside the identity, never a "Sample " prefix inside
                      the address label. The label is the user's own text on a real record,
                      and prefixing it would edit their data to carry a system annotation.
                      Renders nothing when `example_kind` is null, so a real address is
                      untouched.
                    */}
                    <ExampleKindBadge kind={address.example_kind} size="dense" />
                  </Stack>
                </Stack>
                {/*
                  VRG-528 — who manages this location.

                  Right-aligned, immediately left of the kebab, so the column reads down
                  the list as one attribute rather than as a second line under each
                  address. `flexShrink: 0` keeps it whole while the address preview
                  (`minWidth: 0`) is the thing that truncates on a narrow window — the
                  address has a detail view behind it, the manager set does not.

                  A FIXED HEIGHT of 24 on all three states (skeleton, avatars, "No
                  managers"), because 24 is the avatar diameter: the row cannot change
                  height as it resolves, and a card with managers cannot be taller than one
                  without (§19 footprint parity).
                */}
                <Stack
                  alignItems="flex-start"
                  justifyContent="center"
                  spacing={1}
                  sx={{ flex: 1, minWidth: 0, pl: 6, pr: 3 }}
                >
                  {/*
                    THE LABEL IS NOT DECORATION. Bare avatars in a card said nothing about
                    what they were — a reader could as easily have taken them for the
                    people who created the address, or last edited it. Naming the column
                    is what makes the value legible, and it is why this reads as a
                    labelled attribute rather than as loose faces.

                    Rendered ABOVE rather than beside, because the card is already a
                    horizontal row: a left-hand label would take width from the address
                    preview, which is the thing that should get it.
                  */}
                  {/*
                    "Manager", not "Location manager" — the column beside it now says
                    "Location", so repeating the word made the pair read as
                    "Location / Location manager" rather than as two columns.
                  */}
                  <Typography variant="body4" color="text.secondary" noWrap>
                    {managersFor(address.id).length === 1 ? 'Manager' : 'Managers'}
                  </Typography>
                  {/*
                    ONE fixed 40px content box for every state, matching
                    `AddressPreview`'s avatar. Without it the two columns had different
                    content heights, and because both are vertically centred the labels
                    landed on different lines — measured 11px apart on one row and 8px
                    on the next, in opposite directions. Two headers that do not share a
                    baseline read as two annotations, not as a header row.
                  */}
                  <Stack sx={{ height: 40, justifyContent: 'center', minWidth: 0 }}>
                  {isLoadingManagerDetail ? (
                    // Same footprint as the loaded row — a 40px avatar and a line of
                    // text — so the card cannot resize as it resolves (§19).
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Skeleton variant="circular" width={40} height={40} />
                      <Skeleton width={110} />
                    </Stack>
                  ) : managersFor(address.id).length > 0 ? (
                    /*
                      ONE manager gets the full `UserCell` — the same one the data
                      tables render (§36), avatar + name + email, so a person looks
                      identical here and in every list they appear in.

                      SEVERAL get the avatar group instead. "First person + N" was the
                      obvious thing and is wrong: it privileges whoever the query
                      happened to return first, names and emails them, and reduces
                      everyone else to a number — for a set whose members are peers. The
                      group treats them equally and keeps the card one row high, which a
                      stack of full cells would not.

                      Bare avatars were rejected earlier for being unreadable, and that
                      was right — but the objection was that nothing said what they
                      WERE. The label above now does, so the group is legible here in a
                      way it was not before.
                    */
                    managersFor(address.id).length === 1 ? (
                      <UserCell user={managersFor(address.id)[0]} />
                    ) : (
                      <UserAvatarGroup users={managersFor(address.id)} size={32} />
                    )
                  ) : (
                    /*
                      QUIET, not loud. Most locations legitimately have no manager today,
                      so a warning colour or an icon here would flag the normal case as a
                      problem — but a silent blank would hide the one state that actually
                      matters operationally, since a location with nobody assigned can
                      never have an arrival confirmed by scan.
                    */
                    <Typography variant="body3" color="text.secondary" noWrap>
                      None assigned
                    </Typography>
                  )}
                  </Stack>
                </Stack>
                <Box sx={{ width: 44 }}>
                  <ActionDropdown items={getActionItems(address)} />
                </Box>
              </Stack>
            ))}
          </Stack>
        </Card>
      )}

      <CreateAddressModal
        open={openAddressModal}
        address={selectedAddress}
        onClose={() => setOpenAddressModal(false)}
        refetch={refetch}
      />
      <DeleteAddressModal
        address={selectedAddress}
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        refetch={refetch}
      />
      <SetDefaultAddressModal
        address={selectedAddress}
        open={openSetDefaultModal}
        onClose={() => setOpenSetDefaultModal(false)}
        refetch={refetch}
      />
    </Stack>
  )
}
